import { getDateLocale } from '../../../lib/base-i18n';

/**
 * 캘린더 화면의 «날짜 글자»를 한 곳에서 만든다 (2026-09-28).
 *
 * ## ⛔ 왜 모으나 — 같은 뜻이 여러 벌이었고, 그중 셋은 한국어가 «박혀» 있었다
 *
 * ```
 * 손수 조립  CalendarPage 일간 제목 「2026년 9월 28일」 · 월간 제목 「2026년 9월」
 *            DayPopover 「9월 28일 (월)」   ← 요일도 ['일','월',…] 배열이 박혀 있었다
 * ⇒ ⛔ 「년」·「월」·「일」과 요일이 글자로 박혀 있어 en·ja 로 바꿔도 «한국어»가 나왔다
 * ```
 *
 * ## ⭐ 고르는 법 — 「지금 ko 글자」와 «한 자도 다르지 않은» 옵션을 골랐다
 *
 * 추측하지 않고 재서 골랐다(2026-09-28 · Node ICU):
 * ```
 * {year,month:'long',day}            ko 「2026년 9월 28일」  = 종전과 «동일»
 * {year,month:'long'}                ko 「2026년 9월」       = 동일
 * {month:'long',day,weekday:'short'} ko 「9월 28일 (월)」    = 동일 (괄호까지)
 * ⇒ 그래서 ko 화면은 «안 바뀌고», en·ja 만 올바르게 바뀐다
 *   en 「September 28, 2026」 · ja 「2026年9月28日」 …
 * ```
 *
 * ## ⛔ 여기 «없는» 것 — 숫자 압축 표기 셋은 일부러 안 옮겼다
 *
 * ```
 * 주간 제목 「9.27 ~ 10.3」 · 미니 달력 머리 「2026. 09」 · 다가올 일정 「09.28」
 * ⇒ 이것들은 «숫자와 점»뿐이라 한국어가 박혀 있지 않다 ⇒ locale 결함이 «없다»
 * ⇒ 그리고 Intl 의 ko 출력은 「9. 28.」·「2026. 9.」처럼 «다른 글자»라, 옮기면 화면이 바뀐다
 * ⇒ ★ 이번 판은 «결함을 고치는 것»이고 «화면을 바꾸는 것»이 아니라, 그 셋은 그대로 둔다
 *   (바꾸려면 그것은 화면 모양 결정이고 사용자가 고를 일이다)
 * ⚠ 이미 locale 을 쓰는 곳(CalendarDayRail · DateTimePicker)도 건드리지 않았다 — 회귀를 줄인다
 * ```
 */

/** 이 화면들의 날짜 글자는 전부 이 한 줄을 지난다 — 「표기는 한 곳」. */
function label(date: Date, options: Intl.DateTimeFormatOptions): string {
  return date.toLocaleDateString(getDateLocale(), options);
}

/** 일간 뷰 제목 — ko 「2026년 9월 28일」. */
export function dayTitleLabel(date: Date): string {
  return label(date, { year: 'numeric', month: 'long', day: 'numeric' });
}

/** 월간 뷰 제목 — ko 「2026년 9월」. */
export function monthTitleLabel(date: Date): string {
  return label(date, { year: 'numeric', month: 'long' });
}

/** 날짜 팝오버 제목 — ko 「9월 28일 (월)」. 요일 글자도 여기서 나온다. */
export function dayWithWeekdayLabel(date: Date): string {
  return label(date, { month: 'long', day: 'numeric', weekday: 'short' });
}
